import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import * as uiActions from '~actions/ui-actions';

import * as identityProviderAPI from '~api/identity-providers';

import * as dialogsTypes from '~components/dialogs/dialog-types';
import ActionButton from '~components/general/action-button';
import IString from '~components/general/i-string';
import Time from '~components/general/time';
import { IdentityProvider } from '~components/identity-providers/identity-provider-prop-types';

import { getDatadogURL } from '~config/index';

import { translate } from '~i18n/localize';

export default function IdentityProviderDetailsProperties({ identityProvider, linkedAccounts, onUpdate }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const datadogURL = getDatadogURL();
  const hasLinkedAccounts = linkedAccounts?.length > 0;

  const handleDataDogLogs = () => {
    const u = `${datadogURL}/logs?query=service%3Alucid-auth idp%3D${identityProvider?.id}&agg_m=count&agg_m_source=base&agg_t=count&cols=host%2Cservice&fromUser=true&messageDisplay=inline&refresh_mode=sliding&storage=hot&stream_sort=desc&viz=stream&live=true`;
    window.open(u, '_blank', 'noopener,noreferrer');
  };

  const handleEdit = (e) => {
    e.preventDefault();

    dispatch(uiActions.openDialog({
      type: dialogsTypes.IDENTITY_PROVIDER_DIALOG,
      data: { identityProvider },
      onSubmit: async (changed) => {
        if (changed && Object.keys(changed).length > 0) {
          try {
            const updatedIdentityProvider = await identityProviderAPI.updateIdentityProvider(identityProvider.id, changed);
            onUpdate(updatedIdentityProvider);
          } catch (error) {
            dispatch(uiActions.setErrorMessage(error));
          }
        }
        dispatch(uiActions.closeDialog());
      }
    }));
  };

  const handleDelete = (e) => {
    e.preventDefault();

    dispatch(uiActions.openDialog({
      type: dialogsTypes.CONFIRM_DIALOG,
      data: {
        purpose: (
          <IString
            stringKey="identityProvider.dialog.delete.confirm.title"
            placeholders={{ name: identityProvider.name }}
          />
        ),
        details: (
          <IString
            stringKey="identityProvider.dialog.delete.confirm.details"
            placeholders={{ name: identityProvider.name }}
          />
        ),
        confirm: async () => {
          if (hasLinkedAccounts) {
            dispatch(uiActions.setErrorMessage(
              translate('identityProvider.dialog.delete.confirm.hasLinkedAccounts')
            ));
          } else {
            try {
              await identityProviderAPI.deleteIdentityProvider(identityProvider.id);
              dispatch(uiActions.closeDialog());
              history.push('/identity-providers');
            } catch (error) {
              dispatch(uiActions.setErrorMessage(error));
            }
          }
        }
      }
    }));
  };

  const handleCreateURL = () => {
    dispatch(uiActions.openDialog({
      type: dialogsTypes.IDENTITY_PROVIDER_URL_DIALOG,
      data: { identityProvider, linkedAccounts }
    }));
  };

  const requiredAttributes = identityProvider.required_attributes
    ? JSON.stringify(identityProvider.required_attributes)
    : null;
  
  return <>
    <div className="sectionTitle flex">
      <IString stringKey={'identityProvider.details.sectionTitles.details'} />
      <div className="actionBar inline showTitles">
        <ActionButton className="edit" title={translate('general.edit')} onClick={handleEdit} />
        <ActionButton className="delete" title={translate('general.delete')} onClick={handleDelete} />
        <ActionButton className="viewLog" title={translate('identityProvider.details.logs')} onClick={handleDataDogLogs} />
        <ActionButton className="resend" title={translate('identityProvider.details.url')} onClick={handleCreateURL} />
      </div>
    </div>
    <table className="propertiesTable">
      <tbody>
        {[ 'id', 'name', 'entity_id' ].map(key => (
          <tr key={key}>
            <th><IString stringKey={`identityProvider.properties.${key}`} /></th>
            <td>{identityProvider[key]}</td>
          </tr>
        ))}
        <tr>
          <th><IString stringKey={'identityProvider.properties.required_attributes'} /></th>
          <td>{requiredAttributes}</td>
        </tr>
        {[ 'id_attribute', 'email_attribute', 'first_name_attribute', 'last_name_attribute' ].map(key => (
          <tr key={key}>
            <th><IString stringKey={`identityProvider.properties.${key}`} /></th>
            <td>{identityProvider[key]}</td>
          </tr>
        ))}
        <tr>
          <th><IString stringKey="general.created_time" /></th>
          <td><Time value={identityProvider.created_time} /></td>
        </tr>
        <tr>
          <th><IString stringKey="general.updated_time" /></th>
          <td><Time value={identityProvider.updated_time} /></td>
        </tr>
      </tbody>
    </table>
  </>;
}

IdentityProviderDetailsProperties.propTypes = {
  identityProvider: IdentityProvider.isRequired,
  linkedAccounts: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      sso_hint: PropTypes.string
    })
  ),
  onUpdate: PropTypes.func.isRequired
};
